import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Box, FormControlLabel, Switch, Grid, TextField, Typography, Divider, Link as MuiLink, Autocomplete } from "@mui/material";
import { validatePhoneNumber, canEditField } from '../hartUtils';
import { US_STATES } from 'constants/states';

// INITIAL FORM DATA - Default values for creating a new client contact
export const clientContactInitialData = {
  id: '',
  client: '',
  client_name: '',
  contact_name: '',
  contact_title: '',
  contact_phone: '',
  contact_email: '',
  address_1: '',
  address_2: '',
  city: '',
  state: '',
  zip_code: '',
  inactive: false,
  notes: '',
};

// FORM VALIDATION
export function validateClientContact(data, isCreating = false) {
  const errors = {};

  // Client required and should automatically be set when creating a new contact
  if (isCreating && !data.client) {
    errors.client = 'Client is required. Please create contact from a Client detail page.';
  }

  // Contact name is required
  if (!data.contact_name || !data.contact_name.trim()) {
    errors.contact_name = 'Contact Name is required';
  }

  // Validate email format if provided
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (data.contact_email && data.contact_email.trim()) {
    if (!emailRegex.test(data.contact_email)) {
      errors.contact_email = 'Please enter a valid email address';
    }
  }

  const phoneError = validatePhoneNumber(data.contact_phone);
  if (phoneError) errors.contact_phone = phoneError;

  return errors;
}

// NULLABLE FIELDS - Fields that should be converted from empty string to null
export const clientContactNullableFields = [
  'contact_title',
  'contact_phone',
  'contact_email',
  'address_1',
  'address_2',
  'city',
  'state',
  'zip_code',
  'notes',
];

export const clientContactPhoneFields = [
  'contact_phone',
];

// Simple FormField that handles both view and edit modes
const FormField = ({ label, value, editable, onChange, error, helperText, required, type, multiline, minRows, maxRows, isEmail, linkTo }) => {
  if (!editable) {
    // View mode
    const displayValue = value || '—';

    if (multiline) {
      return (
        <>
          <Typography variant="body2" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
            {displayValue && displayValue !== '—' && String(displayValue).trim() ? displayValue : '—'}
          </Typography>
        </>
      );
    }

    // Render value as link (internal route)
    if (linkTo && displayValue !== '—') {
      return (
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600, display: 'block', mb: 0.5 }}>
            {label}
          </Typography>
          <MuiLink
            component={Link}
            to={linkTo}
            underline="always"
            sx={{
              color: 'secondary.main',
              textDecorationColor: (theme) => theme.palette.secondary.main,
              '&:hover': {
                color: 'primary.main',
                textDecorationColor: (theme) => theme.palette.primary.main,
              }
            }}
          >
            {displayValue}
          </MuiLink>
        </Box>
      );
    }

    // Render as mailto link
    if (isEmail && displayValue !== '—') {
      return (
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600, display: 'block', mb: 0.5 }}>
            {label}
          </Typography>
          <MuiLink
            href={`mailto:${displayValue}`}
            underline="always"
            sx={{
              color: 'secondary.main',
              textDecorationColor: (theme) => theme.palette.secondary.main,
              '&:hover': {
                color: 'primary.main',
                textDecorationColor: (theme) => theme.palette.primary.main,
              }
            }}
          >
            {displayValue}
          </MuiLink>
        </Box>
      );
    }

    return (
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="body2" sx={{ color: error ? 'error.main' : 'primary.main', fontWeight: 600, display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="body1" sx={{ color: error ? 'error.main' : 'text.primary' }}>
          {displayValue}
        </Typography>
        {(helperText || error) && (
          <Typography variant="caption" sx={{ color: error ? 'error.main' : 'text.secondary', display: 'block', mt: 0.5 }}>
            {error || helperText}
          </Typography>
        )}
      </Box>
    );
  }

  // Edit mode
  return (
    <TextField
      label={label}
      value={value || ''}
      fullWidth
      required={required}
      type={type}
      multiline={multiline}
      minRows={minRows}
      maxRows={maxRows}
      onChange={onChange}
      variant="outlined"
      error={!!error}
      helperText={error || helperText || ''}
      InputLabelProps={{
        sx: { color: 'primary.main', fontWeight: 600 }
      }}
    />
  );
};

const ClientContactFormFields = ({ data, editable, onChange, errors = {}, displayName, projectId, isCreating = false }) => {
  const gridSpacing = editable ? 3 : 4;
  const gridBreakpoint = editable ? 'sm' : 'md';

  const fieldEditable = (fieldName) => {
    return editable && canEditField(fieldName, 'client_contact', projectId);
  };

  const clientDetailUrl = data.client ? `/projects/${projectId}/hart/clients/${data.client}` : null;

  return (
    <Box component={editable ? "form" : "div"} sx={{ mt: 0, mb: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Typography variant="h2" sx={{ mb: 0 }}>
          {displayName} Details
        </Typography>
        {fieldEditable('inactive') ? (
          <FormControlLabel
            control={
              <Switch
                checked={data.inactive || false}
                onChange={(e) => onChange({ ...data, inactive: e.target.checked })}
                size="small"
              />
            }
            label="Inactive"
            sx={{
              mr: 0,
              ml: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: '0.875rem',
              }
            }}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {data.inactive ? 'Inactive' : 'Active'}
            </Typography>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: data.inactive ? 'error.main' : 'success.main',
              }}
            />
          </Box>
        )}
      </Box>

      {/* Client (read-only) */}
      <Grid container spacing={gridSpacing} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <FormField
            label="Client"
            value={data.client_name || '—'}
            editable={false}
            onChange={() => { }}
            linkTo={clientDetailUrl}
            helperText={errors.client || (isCreating ? 'Contact must be created from a Client detail page' : undefined)}
            error={errors.client}
          />
        </Grid>
      </Grid>

      <Grid container spacing={gridSpacing}>
        {/* Row 1: Contact Name | Contact Title */}
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Contact Name"
            value={data.contact_name}
            editable={fieldEditable('contact_name')}
            onChange={(e) => onChange({ ...data, contact_name: e.target.value })}
            error={errors.contact_name}
            required
          />
        </Grid>
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Contact Title"
            value={data.contact_title}
            editable={fieldEditable('contact_title')}
            onChange={(e) => onChange({ ...data, contact_title: e.target.value })}
          />
        </Grid>

        {/* Row 2: Contact Phone | Contact Email */}
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Contact Phone"
            value={data.contact_phone}
            editable={fieldEditable('contact_phone')}
            onChange={(e) => onChange({ ...data, contact_phone: e.target.value })}
            type="tel"
            error={errors.contact_phone}
          />
        </Grid>
        <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
          <FormField
            label="Contact Email"
            value={data.contact_email}
            editable={fieldEditable('contact_email')}
            onChange={(e) => onChange({ ...data, contact_email: e.target.value })}
            type="email"
            error={errors.contact_email}
            isEmail
          />
        </Grid>

        {/* Address Section */}
        <Grid item xs={12}>
          <Divider sx={{ my: editable ? 2 : 3 }} />
        </Grid>

        {/* Row 3: Address Line 1 */}
        <Grid item xs={12}>
          <FormField
            label="Address Line 1"
            value={data.address_1}
            editable={fieldEditable('address_1')}
            onChange={(e) => onChange({ ...data, address_1: e.target.value })}
          />
        </Grid>

        {/* Row 4: Address Line 2 */}
        <Grid item xs={12}>
          <FormField
            label="Address Line 2"
            value={data.address_2}
            editable={fieldEditable('address_2')}
            onChange={(e) => onChange({ ...data, address_2: e.target.value })}
          />
        </Grid>

        {/* Row 5: City | State | Zip */}
        <Grid item xs={12} sm={4}>
          <FormField
            label="City"
            value={data.city}
            editable={fieldEditable('city')}
            onChange={(e) => onChange({ ...data, city: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          {fieldEditable('state') ? (
            <Autocomplete
              options={US_STATES}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return `${option.abbr} - ${option.name}`;
              }}
              value={US_STATES.find(s => s.abbr === data.state) || (data.state ? data.state : null)}
              onChange={(e, newValue) => {
                onChange({ ...data, state: newValue ? (typeof newValue === 'string' ? newValue : newValue.abbr) : '' });
              }}
              isOptionEqualToValue={(option, value) => {
                if (typeof value === 'string') return option.abbr === value;
                return option.abbr === value?.abbr;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="State"
                  InputLabelProps={{ sx: { color: 'primary.main', fontWeight: 600 } }}
                />
              )}
            />
          ) : (
            <FormField
              label="State"
              value={data.state}
              editable={false}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormField
            label="Zip Code"
            value={data.zip_code}
            editable={fieldEditable('zip_code')}
            onChange={(e) => onChange({ ...data, zip_code: e.target.value })}
          />
        </Grid>

        {/* Notes (full width) */}
        <Grid item xs={12}>
          <Divider sx={{ my: editable ? 2 : 3 }} />
          <FormField
            label="Notes"
            value={data.notes}
            editable={fieldEditable('notes')}
            onChange={(e) => onChange({ ...data, notes: e.target.value })}
            multiline
            minRows={4}
            maxRows={20}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

ClientContactFormFields.propTypes = {
  data: PropTypes.object.isRequired,
  editable: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  displayName: PropTypes.string,
  projectId: PropTypes.string,
  isCreating: PropTypes.bool,
};

export default ClientContactFormFields;
